import { z } from 'zod';
import {
  ACTIVITY_STATUSES,
  ACTIVITY_TYPES,
  CALL_DIRECTIONS,
  PRIORITIES,
  type ActivityStatus,
  type ActivityType,
  type CallDirection,
  type Priority,
} from '../enums.js';
import {
  optionalDateTime,
  optionalNumber,
  optionalText,
  optionalUrl,
  paginationQuerySchema,
  uuidSchema,
} from './common.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

/**
 * One attendee: a colleague, somebody on file, or an address for a person who
 * is neither. Exactly one — an attendee who is two of them is ambiguous about
 * who was actually in the room, and one who is none cannot be invited.
 */
export const attendeeSchema = z
  .object({
    userId: uuidSchema.nullish(),
    contactId: uuidSchema.nullish(),
    email: z.email().max(254).nullish(),
    /** Only for an `email` attendee; a user or contact brings their own name. */
    name: optionalText(120),
  })
  .refine(
    (value) =>
      [value.userId, value.contactId, value.email].filter(
        (identity) => identity !== null && identity !== undefined && identity !== '',
      ).length === 1,
    { message: 'Name a colleague, a contact, or an email address — one of the three' },
  );
export type AttendeeInput = z.infer<typeof attendeeSchema>;

/** Attendees are replaced wholesale, never patched one at a time. */
const attendees = z.array(attendeeSchema).max(100);

const relatedIds = {
  leadId: uuidSchema.nullish(),
  accountId: uuidSchema.nullish(),
  contactId: uuidSchema.nullish(),
  dealId: uuidSchema.nullish(),
};

export const createActivitySchema = z
  .object({
    type: z.enum(ACTIVITY_TYPES).default('TASK'),
    subject: z.string().trim().min(1, 'Required').max(200),
    description: optionalText(4000),
    status: z.enum(ACTIVITY_STATUSES).default('NOT_STARTED'),
    priority: z.enum(PRIORITIES).default('NORMAL'),
    dueAt: optionalDateTime(),
    startAt: optionalDateTime(),
    endAt: optionalDateTime(),
    allDay: z.coerce.boolean().optional(),
    location: optionalText(200),
    remindAt: optionalDateTime(),
    /** Meetings: where to join, when it happens on a screen. */
    meetingUrl: optionalUrl(),
    /** Calls: which way it went. */
    direction: z.enum(CALL_DIRECTIONS).nullish(),
    outcome: optionalText(1000),
    durationMinutes: optionalNumber(z.coerce.number().int().min(0).max(10_080)),
    attendees: attendees.optional(),
    businessManagerId: uuidSchema.optional(),
    customFields: z.record(z.string(), z.unknown()).optional(),
    ...relatedIds,
  })
  .refine((value) => !value.startAt || !value.endAt || value.endAt >= value.startAt, {
    message: 'The end time must be after the start time',
    path: ['endAt'],
  })
  .refine(
    (value) => value.type === 'TASK' || Boolean(value.startAt),
    // A calendar entry with no start time cannot be placed on the calendar.
    { message: 'Calls and meetings need a start time', path: ['startAt'] },
  )
  .refine(
    (value) => value.type !== 'MEETING' || Boolean(value.description),
    // The notes are what a meeting is for; a meeting with none is a calendar slot.
    { message: 'Meeting Notes are required', path: ['description'] },
  );
export type CreateActivityInput = z.infer<typeof createActivitySchema>;

/**
 * Built from the same field set rather than `.partial()` on the refined
 * schema: a ZodEffects (what `.refine()` returns) has no `.partial()`, and the
 * cross-field rules cannot be checked against a patch that omits one side.
 */
export const updateActivitySchema = z.object({
  type: z.enum(ACTIVITY_TYPES).optional(),
  subject: z.string().trim().min(1).max(200).optional(),
  description: optionalText(4000),
  status: z.enum(ACTIVITY_STATUSES).optional(),
  priority: z.enum(PRIORITIES).optional(),
  dueAt: optionalDateTime(),
  startAt: optionalDateTime(),
  endAt: optionalDateTime(),
  allDay: z.coerce.boolean().optional(),
  location: optionalText(200),
  remindAt: optionalDateTime(),
  meetingUrl: optionalUrl(),
  direction: z.enum(CALL_DIRECTIONS).nullish(),
  outcome: optionalText(1000),
  durationMinutes: optionalNumber(z.coerce.number().int().min(0).max(10_080)),
  attendees: attendees.optional(),
  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
  ...relatedIds,
});
export type UpdateActivityInput = z.infer<typeof updateActivitySchema>;

export const ACTIVITY_SORT_FIELDS = [
  'dueAt',
  'startAt',
  'createdAt',
  'priority',
  'subject',
] as const;

export const listActivitiesQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(ACTIVITY_SORT_FIELDS).default('dueAt'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
  type: z.enum(ACTIVITY_TYPES).optional(),
  status: z.enum(ACTIVITY_STATUSES).optional(),
  priority: z.enum(PRIORITIES).optional(),
  direction: z.enum(CALL_DIRECTIONS).optional(),
  businessManagerId: businessManagerFilterSchema.optional(),
  /** Restrict to activities attached to one record. */
  leadId: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  contactId: uuidSchema.optional(),
  dealId: uuidSchema.optional(),
  dueBefore: z.iso.datetime({ offset: true }).optional(),
  dueAfter: z.iso.datetime({ offset: true }).optional(),
  /** Not-yet-complete items whose due date has passed. */
  overdueOnly: z.stringbool().optional(),
  openOnly: z.stringbool().optional(),
});
export type ListActivitiesQuery = z.infer<typeof listActivitiesQuerySchema>;

/** Calendar view: a bounded window, so the query can never be unbounded. */
export const calendarQuerySchema = z
  .object({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
    businessManagerId: businessManagerFilterSchema.optional(),
    type: z.enum(ACTIVITY_TYPES).optional(),
  })
  .refine((value) => new Date(value.to) > new Date(value.from), {
    message: '`to` must be after `from`',
    path: ['to'],
  })
  .refine(
    (value) => new Date(value.to).getTime() - new Date(value.from).getTime() <= 366 * 86_400_000,
    { message: 'Request at most one year at a time', path: ['to'] },
  );
export type CalendarQuery = z.infer<typeof calendarQuerySchema>;

export interface AttendeeView {
  id: string;
  /** How to render them, whichever of the three they are. */
  name: string;
  email: string | null;
  kind: 'USER' | 'CONTACT' | 'EXTERNAL';
  /** Set for a colleague or a contact, so the client can link to them. */
  recordId: string | null;
}

export interface ActivitySummary {
  id: string;
  /** MTG-00042: set on meetings, null on tasks and calls. */
  meetingId: string | null;
  type: ActivityType;
  subject: string;
  status: ActivityStatus;
  priority: Priority;
  dueAt: string | null;
  startAt: string | null;
  endAt: string | null;
  allDay: boolean;
  location: string | null;
  completedAt: string | null;
  /** Calls carry theirs on the summary: it is the column a call list needs. */
  direction: CallDirection | null;
  durationMinutes: number | null;
  /** Meetings carry theirs: the guest count is what a meeting list shows. */
  attendeeCount: number;
  /** Derived server-side so every client agrees on what "overdue" means. */
  isOverdue: boolean;
  businessManager: BusinessManagerRef | null;
  relatedTo: { type: 'LEAD' | 'ACCOUNT' | 'CONTACT' | 'DEAL'; record: RecordRef } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityDetail extends ActivitySummary {
  description: string | null;
  outcome: string | null;
  remindAt: string | null;
  meetingUrl: string | null;
  attendees: AttendeeView[];
  customFields: Record<string, unknown>;
  lead: RecordRef | null;
  account: RecordRef | null;
  contact: RecordRef | null;
  deal: RecordRef | null;
}

/** The "My Day" panel: what is late, what is due today, what is next. */
export interface MyDay {
  overdue: ActivitySummary[];
  dueToday: ActivitySummary[];
  upcoming: ActivitySummary[];
  completedToday: number;
}
